import type { BookDetail, ChapterReview, QualityFinding } from "@contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { api } from "../../../api/fetcher";
import { queryKeys } from "../../../api/keys";
import { Button } from "../../../components/control/Button";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { useBookDetail } from "../bookContext";

export function QualityPanel({
  review,
  onLocate,
  draftPending = false,
}: {
  review: ChapterReview;
  draftPending?: boolean;
  onLocate: (index: number) => void;
}) {
  const book = useBookDetail();
  const { t } = useTranslation("review");
  const cache = useQueryClient();
  const navigate = useNavigate();
  const report = book.qualityReviews?.find(
    (q) => q.chapterId === review.chapterId && q.versionId === review.script?.version.id,
  );
  const unapplied =
    draftPending ||
    book.drafts?.some(
      (d) => !d.key.startsWith("chapter-") || d.key === `chapter-${review.chapterId}`,
    );
  const mutation = useMutation({
    mutationFn: (finding: QualityFinding | null) =>
      api<BookDetail>(
        finding
          ? `/books/${book.id}/quality/resolve`
          : `/books/${book.id}/chapters/${review.chapterId}/quality`,
        { method: "POST", body: finding ? { reviewId: report?.id, findingId: finding.id } : {} },
      ),
    onSuccess: async (data, resolved) => {
      cache.setQueryData(queryKeys.book(book.id), data);
      await cache.invalidateQueries({
        queryKey: queryKeys.chapterReview(book.id, review.chapterId),
      });
      if (
        resolved &&
        !data.qualityReviews
          ?.find((q) => q.chapterId === review.chapterId)
          ?.findings.some((f) => !f.accepted)
      ) {
        const next = data.qualityReviews?.find(
          (q) => q.chapterId !== review.chapterId && q.status === "attention",
        );
        if (next) navigate(`/books/${book.id}/review/${next.chapterId}`);
      }
    },
  });
  if (book.reviewPolicy !== "exceptions") return null;
  const finding = report?.findings.find((f) => !f.accepted);
  const status = report?.status ?? "missing";
  return (
    <section
      className="max-h-[45vh] shrink-0 overflow-auto border-b border-line bg-surface px-4 py-3 md:px-9"
      aria-label={t("quality.title")}
    >
      <p className="font-medium" role="status">
        {t(`quality.status.${status}`)}
      </p>
      {review.isCalibration && !book.casting.narrator ? (
        <p className="mt-2">
          <Link className="underline" to={`/books/${book.id}/casting`}>
            {t("quality.chooseVoice")}
          </Link>
        </p>
      ) : null}
      {report?.repairAttempted ? (
        <p className="text-data text-ink-2">{t("quality.repaired")}</p>
      ) : null}
      {finding ? (
        <div className="mt-2">
          <p>{finding.explanation || t(`quality.code.${finding.code}`)}</p>
          <p className="mt-1 text-data text-ink-2">
            {t("quality.remaining", {
              count: report?.findings.filter((f) => !f.accepted).length ?? 0,
            })}
          </p>
          <details className="mt-2">
            <summary className="cursor-pointer underline">{t("quality.compare")}</summary>
            <div className="mt-2 grid min-w-0 gap-4 md:grid-cols-2">
              <div>
                <h3 className="font-medium">{t("manuscript")}</h3>
                <p className="whitespace-pre-wrap font-serif">
                  {[
                    ...(review.script?.pairs.flatMap((p) => p.nodes) ?? []),
                    ...(review.script?.droppedNodes ?? []),
                  ]
                    .filter(
                      (n, i, a) =>
                        finding.nodeIds.includes(n.id) && a.findIndex((x) => x.id === n.id) === i,
                    )
                    .map((n) => n.strippedText)
                    .join("\n\n")}
                </p>
              </div>
              <div>
                <h3 className="font-medium">{t("script")}</h3>
                <p className="whitespace-pre-wrap font-serif">
                  {review.script?.blocks
                    .filter((b) => finding.blockIndices.includes(b.index))
                    .map((b) => b.text)
                    .join("\n\n")}
                </p>
              </div>
            </div>
          </details>
          <div className="mt-3 flex flex-wrap gap-2">
            {finding.blockIndices[0] !== undefined ? (
              <Button variant="secondary" onClick={() => onLocate(finding.blockIndices[0] ?? 0)}>
                {t("quality.edit")}
              </Button>
            ) : null}
            {!finding.technical ? (
              <Button
                variant="secondary"
                busy={mutation.isPending}
                disabled={unapplied}
                onClick={() => mutation.mutate(finding)}
              >
                {t("quality.accept")}
              </Button>
            ) : (
              <p className="text-ink-2">{t("quality.mustRepair")}</p>
            )}
          </div>
        </div>
      ) : null}
      {["missing", "failed", "stale"].includes(status) ? (
        <Button
          className="mt-2"
          variant="secondary"
          busy={mutation.isPending}
          disabled={unapplied}
          onClick={() => mutation.mutate(null)}
        >
          {t("quality.check")}
        </Button>
      ) : null}
      {unapplied ? <p className="mt-2 text-ink-2">{t("errors:UNAPPLIED_CHANGES")}</p> : null}
      <ErrorBanner error={mutation.error} />
    </section>
  );
}
